import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import {
  FaChartPie, FaSpinner, FaMoneyBillWave, FaFileInvoice,
  FaExclamationTriangle, FaLink, FaUnlink, FaCheck, FaSyncAlt
} from 'react-icons/fa';
import AdminLayout from '../layouts/AdminLayout';
import { Link } from 'react-router-dom';
import { API_URL } from '../config';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);

const RANDS = (v) => 'R ' + (parseFloat(v || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function AccountsOverviewPage({ onLogout }) {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = ['admin', 'super_admin'].includes(user.role);

  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState('');

  const [qb, setQb] = useState({ connected: false, configured: false, environment: 'production' });
  const [qbLoading, setQbLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [disconnectModal, setDisconnectModal] = useState(false);
  const [syncLog, setSyncLog] = useState([]);
  const [showSyncLog, setShowSyncLog] = useState(false);

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    try {
      const qs = month ? `?month=${month}` : '';
      const response = await fetch(`${API_URL}/accounts/overview${qs}`);
      const data = await response.json();
      if (data.success) setOverview(data);
    } catch (error) {
      console.error('Error fetching accounts overview:', error);
      toast.error('Failed to load accounts overview');
    } finally {
      setLoading(false);
    }
  }, [month]);

  const fetchQb = useCallback(async () => {
    setQbLoading(true);
    try {
      const response = await fetch(`${API_URL}/quickbooks/status`);
      const data = await response.json();
      if (data.success) {
        setQb({ connected: data.connected, configured: data.configured, environment: data.environment, connection: data.connection });
      }
    } catch (error) {
      console.error('Error fetching QuickBooks status:', error);
    } finally {
      setQbLoading(false);
    }
  }, []);

  const [profitRows, setProfitRows] = useState([]);
  const [profitLoading, setProfitLoading] = useState(true);
  const [expandedRow, setExpandedRow] = useState(null);

  const fetchProfit = useCallback(async () => {
    setProfitLoading(true);
    try {
      const response = await fetch(`${API_URL}/accounts/job-profitability?months=12`);
      const data = await response.json();
      if (data.success) setProfitRows(data.rows || []);
    } catch (error) {
      console.error('Error fetching job profitability:', error);
    } finally {
      setProfitLoading(false);
    }
  }, []);

  useEffect(() => { fetchOverview(); fetchQb(); fetchProfit(); }, [fetchOverview, fetchQb, fetchProfit]);

  const connectQb = async () => {
    setConnecting(true);
    try {
      const response = await fetch(`${API_URL}/quickbooks/connect`);
      const data = await response.json();
      if (data.success && data.auth_url) {
        window.open(data.auth_url, '_blank', 'noopener');
        toast.success('QuickBooks login opened in a new tab. Complete it there, then return here.');
      } else {
        toast.error(data.error || 'Unable to start QuickBooks connection');
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to start QuickBooks connection');
    } finally {
      setConnecting(false);
    }
  };

  const disconnectQb = async () => {
    try {
      const response = await fetch(`${API_URL}/quickbooks/disconnect`, { method: 'POST' });
      const data = await response.json();
      if (data.success) {
        toast.success('QuickBooks disconnected');
        setDisconnectModal(false);
        fetchQb();
      } else {
        toast.error(data.error || 'Failed to disconnect');
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to disconnect');
    }
  };

  const runSync = async (types) => {
    setSyncing(true);
    try {
      const response = await fetch(`${API_URL}/quickbooks/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ types })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Sync complete — see results in the sync log');
        fetchQb();
      } else {
        toast.error(data.error || 'Sync failed');
      }
    } catch (error) {
      console.error(error);
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const loadSyncLog = async () => {
    setShowSyncLog(true);
    try {
      const response = await fetch(`${API_URL}/quickbooks/sync-log?limit=30`);
      const data = await response.json();
      if (data.success) setSyncLog(data.log || []);
    } catch (error) {
      console.error(error);
      setSyncLog([]);
    }
  };

  const kpis = overview?.kpis || {};


  const KpiCard = ({ icon, label, value, color, sub }) => (
    <div className="bg-white rounded-2xl shadow-sm p-5 border-t-4" style={{ borderColor: color }}>
      <div className="flex items-center gap-3 mb-2">
        <span className="text-gray-400">{icon}</span>
        <span className="text-sm text-gray-500">{label}</span>
      </div>
      <div className="text-2xl font-bold text-navy">{value}</div>
      {sub && <div className="mt-1 text-xs text-gray-400">{sub}</div>}
    </div>
  );

  // ── Charts: where the invoiced money is ──
  const receivedTotal = (parseFloat(kpis.collected) || 0) + (parseFloat(kpis.credits_applied) || 0);
  const moneySplit = [parseFloat(kpis.collected) || 0, parseFloat(kpis.credits_applied) || 0, parseFloat(kpis.outstanding) || 0];
  const moneyChart = {
    labels: ['Cash received', 'Settled by credit', 'Still outstanding'],
    datasets: [{
      data: moneySplit,
      backgroundColor: ['#22C55E', '#14B8A6', '#F59E0B'],
      borderWidth: 0
    }]
  };
  const moneyHasData = moneySplit.some((v) => v > 0);
  const donutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '62%',
    plugins: { legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 11 } } } }
  };
  const marginPct = (parseFloat(kpis.invoiced_ex_vat) || 0) > 0
    ? ((parseFloat(kpis.gross_profit) || 0) / parseFloat(kpis.invoiced_ex_vat)) * 100
    : 0;
  const subbieOwedExVat = parseFloat(kpis.owed_to_subbies_ex_vat) || 0;
  const subbiePaid = parseFloat(kpis.paid_to_subbies) || 0;
  const subbieCost = parseFloat(kpis.cost) || 0;

  const AgedCard = ({ title, buckets }) => (
    <div className="bg-white rounded-2xl shadow-sm p-5">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">{title}</h3>
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-gray-50 rounded-xl p-3">
          <div className="text-xs text-gray-500">Current</div>
          <div className="text-lg font-semibold text-gray-900">{RANDS(buckets.current)}</div>
        </div>
        <div className="bg-gray-50 rounded-xl p-3">
          <div className="text-xs text-gray-500">0–30 days</div>
          <div className="text-lg font-semibold text-gray-900">{RANDS(buckets.d30)}</div>
        </div>
        <div className="bg-amber-50 rounded-xl p-3">
          <div className="text-xs text-amber-700">31–60 days</div>
          <div className="text-lg font-semibold text-amber-800">{RANDS(buckets.d60)}</div>
        </div>
        <div className="bg-red-50 rounded-xl p-3">
          <div className="text-xs text-red-700">60+ days</div>
          <div className="text-lg font-semibold text-red-800">{RANDS(buckets.d90plus)}</div>
        </div>
      </div>
    </div>
  );

  return (
    <AdminLayout activeTab="accounts" onLogout={onLogout}>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-navy flex items-center gap-3">
            <FaChartPie className="text-green-600" /> Accounts Overview
          </h1>
          <p className="text-sm text-gray-500 mt-1">Invoiced · collected · outstanding · owed to subbies · gross profit</p>
        </div>
        <div className="flex items-center gap-3">
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="border border-gray-200 rounded-xl px-3 py-2 text-sm"
          />
          {month && (
            <button onClick={() => setMonth('')} className="px-3 py-2 text-sm text-gray-500 hover:bg-gray-100 rounded-xl">All</button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-gray-400"><FaSpinner className="animate-spin inline" size={24} /></div>
      ) : (
        <>
          {/* Headline numbers — 4 only, VAT stated once */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <KpiCard
              icon={<FaFileInvoice size={18} />}
              label="Invoiced"
              value={RANDS(kpis.invoiced_ex_vat)}
              sub={`excl VAT · ${RANDS(kpis.invoiced)} incl`}
              color="#3B82F6"
            />
            <KpiCard
              icon={<FaMoneyBillWave size={18} />}
              label="Received"
              value={RANDS(receivedTotal)}
              sub={`cash ${RANDS(kpis.collected)} · credit ${RANDS(kpis.credits_applied)}`}
              color="#22C55E"
            />
            <KpiCard
              icon={<FaExclamationTriangle size={18} />}
              label="Outstanding"
              value={RANDS(kpis.outstanding)}
              sub="incl VAT · still to collect"
              color="#F59E0B"
            />
            <KpiCard
              icon={<FaChartPie size={18} />}
              label="Gross profit"
              value={RANDS(kpis.gross_profit)}
              sub={`excl VAT · ${marginPct.toFixed(1)}% margin`}
              color="#8B5CF6"
            />
          </div>

          {/* Where the money is + the subbie position */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <div className="bg-white rounded-2xl shadow-sm p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Where the invoiced money is</h3>
              <div style={{ height: '210px' }}>
                {moneyHasData
                  ? <Doughnut data={moneyChart} options={donutOptions} />
                  : <p className="text-sm text-gray-400 pt-8 text-center">No invoices in this period</p>}
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Subcontractor position</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between border-b border-gray-100 pb-2">
                  <span className="text-sm text-gray-500">Cost on invoiced jobs <span className="text-xs text-gray-400">(excl VAT)</span></span>
                  <span className="font-semibold text-navy">{RANDS(subbieCost)}</span>
                </div>
                <div className="flex items-center justify-between border-b border-gray-100 pb-2">
                  <span className="text-sm text-gray-500">Already paid to subbies</span>
                  <span className="font-semibold text-green-600">-{RANDS(subbiePaid)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Owed to subbies <span className="text-xs text-gray-400">(excl VAT)</span></span>
                  <span className="text-lg font-bold text-red-600">{RANDS(subbieOwedExVat)}</span>
                </div>
                <p className="text-[11px] text-gray-400">
                  Owed includes VAT of {RANDS(parseFloat(kpis.subbie_vat) || 0)} → {RANDS(kpis.owed_to_subbies)} incl VAT.
                  Cost becomes owed once the subbie is invoiced; paying it moves the amount across, it is never counted twice.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <AgedCard title="Aged Debtors (clients owe us)" buckets={overview?.aged?.debtors || { current: 0, d30: 0, d60: 0, d90plus: 0 }} />
            <AgedCard title="Aged Creditors (we owe subbies)" buckets={overview?.aged?.creditors || { current: 0, d30: 0, d60: 0, d90plus: 0 }} />
          </div>
        </>
      )}

      {/* Job profitability — internal cost per line item */}
      <div className="bg-white rounded-2xl shadow-sm p-5 mb-6 border" style={{ borderColor: '#E8ECEF' }}>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
            <FaChartPie className="text-green-600" /> Job Profitability
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">Internal · last 12 months</span>
          </h3>
          <span className="text-xs text-gray-400">Click a row to see cost per line item</span>
        </div>

        {profitLoading ? (
          <div className="py-8 text-center text-gray-400"><FaSpinner className="animate-spin inline" size={20} /></div>
        ) : profitRows.length === 0 ? (
          <p className="text-center text-gray-400 py-8">No invoices in the last 12 months</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left px-3 py-2 text-xs text-gray-500 uppercase">Invoice</th>
                  <th className="text-left px-3 py-2 text-xs text-gray-500 uppercase">Job</th>
                  <th className="text-left px-3 py-2 text-xs text-gray-500 uppercase">Client</th>
                  <th className="text-right px-3 py-2 text-xs text-gray-500 uppercase">Invoiced (excl VAT)</th>
                  <th className="text-right px-3 py-2 text-xs text-gray-500 uppercase">VAT</th>
                  <th className="text-right px-3 py-2 text-xs text-gray-500 uppercase">Cost</th>
                  <th className="text-right px-3 py-2 text-xs text-gray-500 uppercase">Margin</th>
                  <th className="text-right px-3 py-2 text-xs text-gray-500 uppercase">Margin %</th>
                </tr>
              </thead>
              <tbody>
                {profitRows.map((row) => {
                  const isOpen = expandedRow === row.id;
                  const marginColor = row.margin_percentage < 0 ? 'text-red-600' : row.margin_percentage < 10 ? 'text-amber-600' : 'text-green-700';
                  return (
                    <React.Fragment key={row.id}>
                      <tr
                        className="border-b border-gray-100 cursor-pointer hover:bg-gray-50"
                        onClick={() => setExpandedRow(isOpen ? null : row.id)}
                      >
                        <td className="px-3 py-2 font-medium text-navy">{row.invoice_number}</td>
                        <td className="px-3 py-2">{row.job_number}</td>
                        <td className="px-3 py-2">{row.client}</td>
                        <td className="px-3 py-2 text-right">{RANDS(row.invoiced)}</td>
                        <td className="px-3 py-2 text-right text-gray-500">{RANDS(row.vat)}</td>
                        <td className="px-3 py-2 text-right">{RANDS(row.cost)}</td>
                        <td className={`px-3 py-2 text-right font-medium ${marginColor}`}>{RANDS(row.profit)}</td>
                        <td className={`px-3 py-2 text-right font-semibold ${marginColor}`}>{row.margin_percentage.toFixed(1)}%</td>
                      </tr>
                      {isOpen && (
                        <tr className="border-b border-gray-100 bg-gray-50/50">
                          <td colSpan={8} className="px-3 py-3">
                            <div className="text-xs text-gray-500 uppercase font-semibold mb-2">Line items — cost & margin</div>
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-gray-400 uppercase">
                                  <th className="text-left py-1">Description</th>
                                  <th className="text-right py-1">Qty</th>
                                  <th className="text-right py-1">Rate</th>
                                  <th className="text-right py-1">Amount</th>
                                  <th className="text-right py-1">Cost</th>
                                  <th className="text-right py-1">Margin</th>
                                </tr>
                              </thead>
                              <tbody>
                                {row.items.map((it, i) => (
                                  <tr key={i} className="border-t border-gray-100">
                                    <td className="py-1">{it.description}</td>
                                    <td className="py-1 text-right">{it.quantity_mt ?? '-'}</td>
                                    <td className="py-1 text-right">{it.rate_per_mt != null ? RANDS(it.rate_per_mt) : '-'}</td>
                                    <td className="py-1 text-right">{RANDS(it.amount)}</td>
                                    <td className="py-1 text-right">{RANDS(it.cost)}</td>
                                    <td className="py-1 text-right">{it.margin_amount ? `${RANDS(it.margin_amount)} (${(it.margin_percentage || 0).toFixed(1)}%)` : '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* QuickBooks card */}
      <div className="bg-white rounded-2xl shadow-sm p-5 mb-4 border" style={{ borderColor: '#E8ECEF' }}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
              QuickBooks Online
              {!qbLoading && (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: qb.connected ? '#D1FAE5' : '#FEF3C7', color: qb.connected ? '#065F46' : '#92400E' }}>
                  {qb.connected ? 'Connected' : 'Not connected'}
                </span>
              )}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {qbLoading ? 'Checking connection…' : qb.connected
                ? `Company ${qb.connection?.realm_id} · last synced ${qb.connection?.last_synced_at ? new Date(qb.connection.last_synced_at).toLocaleString() : 'never'}`
                : qb.configured ? 'Connect your QuickBooks company to sync customers, invoices and payments.' : 'QuickBooks is not configured yet — add QBO_CLIENT_ID / QBO_CLIENT_SECRET to the backend .env.'}
            </p>
            <Link to="/help/accounting" className="text-sm text-green font-medium hover:underline mt-1 inline-block">
              📘 How the accounting &amp; QuickBooks sync works (guide you can send your accountant)
            </Link>
          </div>
          <div className="flex gap-3">
            {isAdmin && !qb.connected && (
              <button
                onClick={connectQb}
                disabled={connecting || !qb.configured}
                className="px-4 py-2 bg-green text-white rounded-lg text-sm font-medium hover:bg-green disabled:opacity-50 flex items-center gap-2"
              >
                {connecting ? <FaSpinner className="animate-spin" size={14} /> : <FaLink size={14} />} Connect QuickBooks
              </button>
            )}
            {isAdmin && qb.connected && (
              <>
                <button onClick={() => runSync(['customers', 'suppliers'])} disabled={syncing} className="px-4 py-2 bg-navy text-white rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50 flex items-center gap-2">
                  {syncing ? <FaSpinner className="animate-spin" size={14} /> : <FaSyncAlt size={14} />} Sync Customers & Suppliers
                </button>
                <button onClick={() => runSync(['invoices', 'payments', 'bills', 'bill_payments'])} disabled={syncing} className="px-4 py-2 bg-navy text-white rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50 flex items-center gap-2">
                  {syncing ? <FaSpinner className="animate-spin" size={14} /> : <FaSyncAlt size={14} />} Sync Invoices & Bills
                </button>
                <button onClick={() => runSync(['credit_memos', 'bill_voids', 'vendor_credits'])} disabled={syncing} className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 disabled:opacity-50 flex items-center gap-2" title="Push credit notes as QBO credit memos (applied to their invoice), void QBO bills for cancelled subbie invoices, and post money returned by subbies as vendor credits">
                  {syncing ? <FaSpinner className="animate-spin" size={14} /> : <FaSyncAlt size={14} />} Sync Credit Notes, Voids & Recoveries
                </button>
                <button onClick={loadSyncLog} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200">
                  Sync Log
                </button>
                <button onClick={() => setDisconnectModal(true)} className="px-4 py-2 bg-red-50 text-red-600 rounded-lg text-sm font-medium hover:bg-red-100 flex items-center gap-2">
                  <FaUnlink size={14} /> Disconnect
                </button>
              </>
            )}
          </div>
        </div>
      </div>


      {/* Disconnect modal */}
      {disconnectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setDisconnectModal(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6">
            <h2 className="text-xl font-bold text-navy mb-2 flex items-center gap-2">
              <FaUnlink className="text-red-500" /> Disconnect QuickBooks
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              This stops syncing to the current QuickBooks company. Existing QBO ID mappings are kept, so reconnecting to the same company resumes safely.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDisconnectModal(false)} className="px-4 py-2 text-sm text-gray-500 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button onClick={disconnectQb} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700">Disconnect</button>
            </div>
          </div>
        </div>
      )}

      {/* Sync log modal */}
      {showSyncLog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowSyncLog(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 p-6">
            <h2 className="text-xl font-bold text-navy mb-2">QuickBooks Sync Log</h2>
            <div className="max-h-96 overflow-y-auto space-y-2">
              {syncLog.map((l) => (
                <div key={l.id} className="border border-gray-100 rounded-xl p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium uppercase" style={{ color: l.status === 'success' ? '#059669' : l.status === 'failed' ? '#DC2626' : '#D97706' }}>
                      {l.status}
                    </span>
                    <span className="text-xs text-gray-400">{new Date(l.created_at).toLocaleString()}</span>
                  </div>
                  <div className="text-sm text-gray-700 mt-1">{l.sync_type} — {l.message}</div>
                </div>
              ))}
              {syncLog.length === 0 && <p className="text-center text-gray-400 py-6">No sync activity yet</p>}
            </div>
            <div className="flex justify-end mt-4">
              <button onClick={() => setShowSyncLog(false)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200">Close</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default AccountsOverviewPage;
